import { useGoogleMap } from "@react-google-maps/api";
import { memo, useEffect, useRef } from "react";
import { createRoot, Root } from "react-dom/client";

type AdvancedMarkerProps = {
  position: google.maps.LatLng;
  children: React.ReactNode;
  onClick?: () => void;
  zIndex?: number;
};

export const AdvancedMarker = memo(
  ({ position, children, onClick, zIndex }: AdvancedMarkerProps) => {
    const markerRef = useRef<google.maps.marker.AdvancedMarkerElement>();
    const map = useGoogleMap();
    const rootRef = useRef<Root>();

    useEffect(() => {
      if (!rootRef.current) {
        const container = document.createElement("div");
        rootRef.current = createRoot(container);

        // eslint-disable-next-line no-undef
        markerRef.current = new google.maps.marker.AdvancedMarkerElement({
          position,
          content: container,
        });
      }

      return () => {
        if (markerRef.current) {
          markerRef.current.map = null;
        }
      };
    }, []);

    useEffect(() => {
      if (!markerRef.current || !rootRef.current) {
        return;
      }

      rootRef.current.render(children);

      markerRef.current.position = position;
      markerRef.current.map = map;
      markerRef.current.zIndex = zIndex;

      let clickListener: google.maps.MapsEventListener;

      if (onClick) {
        clickListener = markerRef.current.addListener("click", onClick);
      }

      return () => {
        if (clickListener) {
          clickListener.remove();
        }
      };
    }, [map, position, children, onClick, zIndex]);

    return null;
  }
);
